/**
 * Command palette (point 14) — hand-rolled subsequence fuzzy search over
 * pages (nav items, bilingual), quick actions, recent cases (localStorage),
 * and the live service/agent catalogs. Ranking: exact-prefix > substring >
 * subsequence. Keyboard-first: arrows navigate, Enter opens, Esc closes.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassInput, useI18n, type Localized } from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { NAV_GROUPS, QUICK_ACTIONS } from '../lib/navigation.js';
import { readRecentCases } from '../lib/recentCases.js';
import { Icon, type IconName } from './elevated/icons.js';

type GroupId = 'pages' | 'actions' | 'cases' | 'services' | 'agents';

interface PaletteItem {
  id: string;
  group: GroupId;
  label: Localized;
  hint?: Localized;
  to: string;
  icon: IconName;
  /** Precomputed lowercase haystack (en + ar + keywords). */
  search: string;
}

interface ServiceCommandRow {
  slug: string;
  name: Localized;
}

interface AgentCommandRow {
  slug: string;
  name: Localized;
  title: Localized;
}

const GROUP_ORDER: GroupId[] = ['pages', 'actions', 'cases', 'services', 'agents'];

const GROUP_TITLE: Record<GroupId, Localized> = {
  pages: { en: 'Pages', ar: 'الصفحات' },
  actions: { en: 'Quick actions', ar: 'إجراءات سريعة' },
  cases: { en: 'Recent cases', ar: 'الحالات الأخيرة' },
  services: { en: 'Services', ar: 'الخدمات' },
  agents: { en: 'Agents', ar: 'الوكلاء' },
};

const MAX_PER_GROUP = 8;

/** Fuzzy rank: 3 exact-prefix, 2 substring, 1 subsequence, 0 no match. */
function rankText(needle: string, text: string): number {
  if (text.startsWith(needle)) return 3;
  if (text.includes(needle)) return 2;
  let index = 0;
  for (const char of text) {
    if (char === needle[index]) index += 1;
    if (index === needle.length) return 1;
  }
  return 0;
}

/** Best rank across the label (both languages, prefix-sensitive) and haystack. */
function rankItem(needle: string, item: PaletteItem): number {
  return Math.max(
    rankText(needle, item.label.en.toLocaleLowerCase()),
    rankText(needle, item.label.ar.toLocaleLowerCase()),
    rankText(needle, item.search),
  );
}

function searchText(parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(' ').toLocaleLowerCase();
}

export function CommandPalette({
  open,
  onClose,
  roles,
}: {
  open: boolean;
  onClose: () => void;
  roles: string[];
}) {
  const { l } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [services, setServices] = useState<ServiceCommandRow[]>([]);
  const [agents, setAgents] = useState<AgentCommandRow[]>([]);
  const [recentCases, setRecentCases] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement>(null);
  const normalizedRoles = useMemo(() => new Set(roles), [roles]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
      return;
    }
    setRecentCases(readRecentCases().map((entry) => entry.reference));
    admin()
      .services()
      .then((response) => setServices((response.services ?? []) as unknown as ServiceCommandRow[]))
      .catch(() => setServices([]));
    admin()
      .agents()
      .then((response) => setAgents(response.agents ?? []))
      .catch(() => setAgents([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open, onClose]);

  const items = useMemo<PaletteItem[]>(() => {
    const allowed = (itemRoles?: string[]) =>
      !itemRoles || itemRoles.some((role) => normalizedRoles.has(role));
    const pages: PaletteItem[] = NAV_GROUPS.flatMap((group) => group.items)
      .filter((item) => allowed(item.roles))
      .map((item) => ({
        id: `page-${item.to}`,
        group: 'pages',
        label: item.label,
        hint: item.hint,
        to: item.to,
        icon: item.icon,
        search: searchText([item.label.en, item.label.ar, item.hint?.en, item.hint?.ar, item.keywords]),
      }));
    const actions: PaletteItem[] = QUICK_ACTIONS.filter((action) => allowed(action.roles)).map((action) => ({
      id: `action-${action.id}`,
      group: 'actions',
      label: action.label,
      hint: action.hint,
      to: action.to,
      icon: action.icon,
      search: searchText([action.label.en, action.label.ar, action.hint.en, action.hint.ar, action.keywords]),
    }));
    const cases: PaletteItem[] = recentCases.map((reference) => ({
      id: `case-${reference}`,
      group: 'cases',
      label: { en: reference, ar: reference },
      hint: { en: 'Open case file', ar: 'فتح ملف الحالة' },
      to: `/cases/${encodeURIComponent(reference)}`,
      icon: 'case' as IconName,
      search: reference.toLocaleLowerCase(),
    }));
    const serviceItems: PaletteItem[] = services.map((service) => ({
      id: `service-${service.slug}`,
      group: 'services',
      label: service.name,
      hint: { en: `Open ${service.slug} in Service Studio`, ar: `فتح ${service.slug} في استوديو الخدمات` },
      to: `/services/${service.slug}`,
      icon: 'services' as IconName,
      search: searchText([service.slug, service.name.en, service.name.ar]),
    }));
    const agentItems: PaletteItem[] = agents.map((agent) => ({
      id: `agent-${agent.slug}`,
      group: 'agents',
      label: agent.name,
      hint: {
        en: `${agent.title.en} · Open agent workspace`,
        ar: `${agent.title.ar} · فتح مساحة عمل الوكيل`,
      },
      to: `/agents/${encodeURIComponent(agent.slug)}?tab=overview`,
      icon: 'agents' as IconName,
      search: searchText([
        agent.slug,
        agent.name.en,
        agent.name.ar,
        agent.title.en,
        agent.title.ar,
        'prompt notes integration automation',
      ]),
    }));
    return [...pages, ...actions, ...cases, ...serviceItems, ...agentItems];
  }, [services, agents, recentCases, normalizedRoles]);

  const needle = query.trim().toLocaleLowerCase();

  // Grouped results: within each group rank desc, then label; group order fixed.
  const grouped = useMemo(() => {
    return GROUP_ORDER.map((group) => {
      const rows = items
        .filter((item) => item.group === group)
        .map((item) => ({ item, rank: needle ? rankItem(needle, item) : 2 }))
        .filter((row) => row.rank > 0)
        .sort((a, b) =>
          needle
            ? b.rank - a.rank || l(a.item.label).localeCompare(l(b.item.label))
            : 0,
        )
        .map((row) => row.item)
        .slice(0, MAX_PER_GROUP);
      return { group, rows };
    }).filter((section) => section.rows.length > 0);
  }, [items, needle, l]);

  // Flat navigation order (arrow keys / Enter) matches the rendered order.
  const flat = useMemo(() => grouped.flatMap((section) => section.rows), [grouped]);

  // Reset the highlight when the result set changes; keep it scroll-visible.
  useEffect(() => {
    setActive(0);
  }, [needle]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const choose = (item: PaletteItem) => {
    navigate(item.to);
    onClose();
  };

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = flat[active];
      if (target) choose(target);
    }
  };

  let runningIndex = -1;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[100] flex items-start justify-center bg-aeblack-900/45 px-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={l({ en: 'Command palette', ar: 'لوحة الأوامر' })}
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-aeblack-200 bg-white shadow-2xl"
      >
        <div className="border-b border-aeblack-100 p-3">
          <GlassInput
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={l({ en: 'Search pages, actions, cases, services, agents…', ar: 'ابحث في الصفحات والإجراءات والحالات والخدمات والوكلاء…' })}
            aria-label={l({ en: 'Search commands', ar: 'البحث في الأوامر' })}
            className="w-full"
          />
        </div>
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-faint">
              {l({ en: 'No matching destination', ar: 'لا توجد وجهة مطابقة' })}
            </div>
          ) : (
            grouped.map((section) => (
              <div key={section.group}>
                <div className="px-3 pt-3 pb-1 text-[10px] font-extrabold tracking-[0.14em] text-ink-faint uppercase">
                  {l(GROUP_TITLE[section.group])}
                </div>
                {section.rows.map((item) => {
                  runningIndex += 1;
                  const index = runningIndex;
                  const isActive = index === active;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-index={index}
                      onClick={() => choose(item)}
                      onMouseEnter={() => setActive(index)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start ${
                        isActive ? 'bg-aegold-50' : 'hover:bg-aegold-50/60'
                      }`}
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-aeblack-800 text-white">
                        <Icon name={item.icon} size={15} />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold">{l(item.label)}</span>
                        {item.hint && <span className="block truncate text-xs text-ink-faint">{l(item.hint)}</span>}
                      </span>
                      {isActive && <span className="ms-auto font-mono text-[10px] text-ink-faint">↵</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-aeblack-100 bg-aeblack-50 px-3 py-2 text-[10px] text-ink-faint">
          <span>
            <kbd className="font-mono">↑↓</kbd> {l({ en: 'to navigate', ar: 'للتنقل' })}
            {' · '}
            <kbd className="font-mono">↵</kbd> {l({ en: 'to open', ar: 'للفتح' })}
            {' · '}
            <kbd className="font-mono">Esc</kbd> {l({ en: 'to close', ar: 'للإغلاق' })}
          </span>
          <span>
            <kbd className="font-mono">?</kbd> {l({ en: 'for shortcuts', ar: 'للاختصارات' })}
          </span>
        </footer>
      </section>
    </div>
  );
}
